// frontend/src/components/shared/InstitutionDropdown.tsx
/**
 * Shared dropdown shell for institution membership pickers.
 *
 * Workstream M item 3 (2026-05-19) — extracted the open-state +
 * outside-click + Escape + membership iteration + accessibility (role,
 * aria-haspopup, aria-expanded, aria-selected, disabled-when-active)
 * scaffolding that SchoolSwitcher (header) and SidebarSchoolDropdown
 * (Profile Settings) duplicated. The styling and trigger/item
 * presentation differ between the two callsites, so the visual shell
 * stays caller-controlled via render props.
 *
 * What the component owns:
 *   - Container `<div ref={containerRef} className={containerClassName}>`
 *   - Open state + outside-click + Escape close
 *   - List `<div role="listbox" className={listClassName} data-testid={...-list}>`
 *   - Per-item `<button role="option" aria-selected disabled data-testid={...-option-${slug}} onClick>`
 *   - Selection handler that calls `onSelect` then closes the dropdown
 *     (caller's `onSelect` can abort by returning false / rejecting)
 *
 * What the caller controls:
 *   - The trigger button: pill vs uppercase header, icon (chevron SVG or
 *     lucide), label, theming
 *   - The per-item INNER content: 2-line layout (short + full name) vs
 *     single-line "Short — Full" — caller's choice via `renderItem`
 *   - Per-item button className: active vs inactive styling
 *
 * Test contracts preserved:
 *   - data-testid pattern: `${testIdPrefix}-trigger` (caller's button)
 *     + `${testIdPrefix}-list` + `${testIdPrefix}-option-${slug}` per item
 */

import { useRef, useState, type ReactNode } from 'react';

import type { Membership } from '../../contexts/ActiveInstitutionContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';

export interface InstitutionDropdownProps {
  memberships: Membership[];
  active: Membership | null;
  /** Called when the user picks an option. May return a Promise; if it
   *  resolves to/returns `false`, the dropdown still closes but no
   *  side-effect runs (caller's responsibility to NOT call setActive
   *  on abort). The component does not pass the abort signal further —
   *  the caller is the single source of truth for "should this switch
   *  happen?" The component just handles UI close. */
  onSelect: (institutionId: number) => void | Promise<void>;
  /** Prefix for data-testid attributes. e.g. 'school-switcher' →
   *  '-trigger', '-list', '-option-${slug}'. */
  testIdPrefix: string;
  containerClassName?: string;
  listClassName?: string;
  /** Per-item button className. Receives { isActive } so the caller can
   *  return different classes for the active row. */
  itemClassName: (args: { isActive: boolean }) => string;
  /** Render the trigger button (caller controls full content + class). */
  renderTrigger: (args: {
    active: Membership | null;
    open: boolean;
    toggle: () => void;
  }) => ReactNode;
  /** Render the INNER content of each option button. The surrounding
   *  `<button role="option" aria-selected disabled data-testid onClick>` is
   *  rendered by this component. */
  renderItem: (args: { membership: Membership; isActive: boolean }) => ReactNode;
}

export const InstitutionDropdown = ({
  memberships,
  active,
  onSelect,
  testIdPrefix,
  containerClassName,
  listClassName,
  itemClassName,
  renderTrigger,
  renderItem,
}: InstitutionDropdownProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape. Both are no-ops when the dropdown is
  // closed (cleaner than always-on listeners).
  useClickOutside(containerRef, () => setOpen(false), open);
  useEscapeKey(() => setOpen(false), open);

  const toggle = () => setOpen((prev) => !prev);

  const handleSelect = async (institutionId: number) => {
    try {
      await onSelect(institutionId);
    } catch {
      // Caller's onSelect failed; close the dropdown anyway. Caller is
      // responsible for surfacing the error elsewhere (toast, log, etc.).
      // We swallow here so an async-thrown error doesn't bubble up as
      // an unhandled rejection from the onClick handler.
    } finally {
      // Always close the dropdown after a selection, regardless of
      // whether the caller's onSelect aborted, succeeded, or threw.
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={containerClassName}>
      {renderTrigger({ active, open, toggle })}

      {open && (
        <div
          role="listbox"
          aria-label="Switch institution"
          data-testid={`${testIdPrefix}-list`}
          className={listClassName}
        >
          {memberships.map((m) => {
            const isActive = m.institution.id === active?.institution.id;
            return (
              <button
                key={m.institution.id}
                type="button"
                role="option"
                aria-selected={isActive}
                disabled={isActive}
                onClick={() => {
                  void handleSelect(m.institution.id);
                }}
                data-testid={`${testIdPrefix}-option-${m.institution.slug}`}
                className={itemClassName({ isActive })}
              >
                {renderItem({ membership: m, isActive })}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
